import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { usersAPI } from '@/lib/api';
import { toast } from 'sonner';
import { UserMinus, Star, Package, ExternalLink, Users } from 'lucide-react';
import { getInitials } from '@/lib/utils';

const DashboardFollowingTab = () => {
    const [following, setFollowing] = useState([]);
    const [loading, setLoading] = useState(true);
    const [unfollowingId, setUnfollowingId] = useState(null);

    useEffect(() => {
        fetchFollowing();
    }, []);

    const fetchFollowing = async () => {
        try {
            setLoading(true);
            const response = await usersAPI.getFollowedSellers();
            setFollowing(response.data || []);
        } catch (error) {
            console.error('Error fetching following:', error);
            toast.error('Eroare la încărcarea utilizatorilor urmăriți');
        } finally {
            setLoading(false);
        }
    };

    const handleUnfollow = async (sellerId, sellerName) => {
        try {
            setUnfollowingId(sellerId);
            await usersAPI.unfollowSeller(sellerId);
            setFollowing(prev => prev.filter(f => f.seller_id !== sellerId));
            toast.success(`Nu mai urmărești pe ${sellerName}`);
        } catch (error) {
            console.error('Error unfollowing:', error);
            toast.error('Eroare la anularea urmăririi');
        } finally {
            setUnfollowingId(null);
        }
    };

    if (loading) {
        return (
            <div className="space-y-4">
                <h2 className="text-2xl font-bold mb-6">Utilizatori Urmăriți</h2>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {[1, 2, 3].map(i => (
                        <Skeleton key={i} className="h-[180px] rounded-xl" />
                    ))}
                </div>
            </div>
        );
    }

    if (following.length === 0) {
        return (
            <div className="text-center py-12">
                <Users className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">Nu urmărești pe nimeni</h3>
                <p className="text-muted-foreground mb-4">
                    Urmărește vânzători pentru a primi notificări când adaugă piese noi.
                </p>
                <Link to="/all-sales">
                    <Button>Explorează vânzători</Button>
                </Link>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">Utilizatori Urmăriți</h2>
                <Badge variant="secondary" className="text-sm">
                    {following.length} {following.length === 1 ? 'urmărit' : 'urmăriți'}
                </Badge>
            </div>
            
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {following.map((seller) => (
                    <Card key={seller.seller_id} className="overflow-hidden hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                            <div className="flex items-start gap-4">
                                <Link to={`/user/${seller.username}`}>
                                    <Avatar className="w-14 h-14 border-2 border-primary/10">
                                        <AvatarImage src={seller.picture} alt={seller.name || seller.username} />
                                        <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                                            {getInitials(seller.name || seller.username)}
                                        </AvatarFallback>
                                    </Avatar>
                                </Link>
                                <div className="flex-1 min-w-0">
                                    <Link 
                                        to={`/user/${seller.username}`}
                                        className="font-semibold text-foreground hover:text-primary transition-colors block truncate"
                                    >
                                        {seller.name || seller.username}
                                    </Link>
                                    <p className="text-sm text-muted-foreground truncate">
                                        @{seller.username}
                                    </p>
                                    
                                    <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
                                        {seller.rating > 0 && (
                                            <div className="flex items-center gap-1">
                                                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                                                <span>{seller.rating.toFixed(1)}</span>
                                            </div>
                                        )}
                                        <div className="flex items-center gap-1">
                                            <Package className="w-4 h-4" />
                                            <span>{seller.active_listings || 0} listări</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex gap-2 mt-4">
                                <Link to={`/user/${seller.username}`} className="flex-1">
                                    <Button variant="outline" size="sm" className="w-full">
                                        <ExternalLink className="w-4 h-4 mr-2" />
                                        Vezi profil
                                    </Button>
                                </Link>
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                    onClick={() => handleUnfollow(seller.seller_id, seller.name || seller.username)}
                                    disabled={unfollowingId === seller.seller_id}
                                >
                                    <UserMinus className="w-4 h-4" />
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
};

export default DashboardFollowingTab;
